import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { testimonials, testimonialStats } from '../../mocks/testimonials';
import TestimoniosSeoJsonLd from './components/SeoJsonLd';

function RatingBar({ label, percentage, count }: { label: string; percentage: number; count: number }) {
  return (
    <div className="flex items-center gap-3 group cursor-default">
      <span className="text-sm text-gray-600 w-20 whitespace-nowrap">{label}</span>
      <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-yellow-400 rounded-full transition-all duration-700 group-hover:bg-yellow-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-sm text-gray-500 w-12 text-right">{count}</span>
    </div>
  );
}

function TestimonialCard({ testimonial, featured = false }: { testimonial: typeof testimonials[0]; featured?: boolean }) {
  return (
    <article className={`bg-white border rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col ${featured ? 'border-[#0078D4]/30 ring-1 ring-[#0078D4]/10' : 'border-gray-200'}`}>
      {featured && (
        <div className="bg-gradient-to-r from-[#0078D4] to-[#005a9e] px-4 py-1.5 flex items-center gap-2">
          <i className="ri-award-fill text-yellow-300 text-xs"></i>
          <span className="text-[10px] sm:text-xs text-white font-medium whitespace-nowrap">Reseña destacada</span>
        </div>
      )}
      <div className="p-4 sm:p-6 flex flex-col flex-1">
        <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full flex-shrink-0 bg-gradient-to-br from-[#0078D4] to-[#005a9e] flex items-center justify-center shadow-md">
            <span className="text-white text-base sm:text-lg font-bold">
              {testimonial.name.charAt(0)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-sm">{testimonial.name}</h3>
              {testimonial.verified && (
                <span className="flex items-center gap-1 text-[10px] sm:text-xs text-green-600 bg-green-50 px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap">
                  <i className="ri-verified-badge-fill text-[10px] sm:text-xs"></i>
                  Verificado
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <i className="ri-map-pin-2-line text-[10px] sm:text-xs text-gray-400"></i>
              <p className="text-[10px] sm:text-xs text-gray-500">{testimonial.location}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <i
                key={star}
                className={`${star <= testimonial.rating ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'} text-sm`}
              />
            ))}
          </div>
          <span className="text-[10px] sm:text-xs text-gray-400">{testimonial.date}</span>
        </div>

        <div className="mb-3">
          <span className="inline-flex items-center text-[10px] sm:text-xs bg-[#0078D4]/8 text-[#0078D4] px-2 py-1 rounded-md font-medium whitespace-nowrap">
            <i className="ri-shopping-bag-line mr-1"></i>
            {testimonial.product}
          </span>
        </div>

        <p className="text-xs sm:text-sm text-gray-700 leading-relaxed flex-1">{testimonial.comment}</p>

        {testimonial.ownerResponse && (
          <div className="mt-4 pt-4 border-t border-gray-100 bg-gradient-to-br from-gray-50 to-white -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 sm:py-4 -mb-4 sm:-mb-6 rounded-b-lg">
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex-shrink-0 bg-gradient-to-br from-[#14B8A6] to-[#0d9488] flex items-center justify-center shadow-sm">
                <i className="ri-store-2-fill text-white text-xs sm:text-sm"></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-gray-900 text-[10px] sm:text-xs">Digital License Store</span>
                  <span className="text-[9px] sm:text-[10px] text-gray-500 bg-white px-1.5 py-0.5 rounded-full border border-gray-200 whitespace-nowrap">Propietario</span>
                </div>
                <p className="text-[10px] sm:text-xs text-gray-600 leading-relaxed">{testimonial.ownerResponse}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

export default function TestimoniosPage() {
  const [filter, setFilter] = useState<'all' | '5' | '4' | '3'>('all');

  const filteredTestimonials = filter === 'all'
    ? testimonials
    : testimonials.filter((t) => t.rating === Number(filter));

  const filters = [
    { key: 'all', label: 'Todos', icon: 'ri-list-check' },
    { key: '5', label: '5 Estrellas', icon: 'ri-star-fill' },
    { key: '4', label: '4 Estrellas', icon: 'ri-star-fill' },
    { key: '3', label: '3 Estrellas', icon: 'ri-star-fill' },
  ] as const;

  const ratingCounts = {
    five: Math.round(testimonialStats.totalReviews * testimonialStats.fiveStars / 100),
    four: Math.round(testimonialStats.totalReviews * testimonialStats.fourStars / 100),
    three: 0,
    two: 0,
    one: 0,
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <TestimoniosSeoJsonLd />
      <Header />

      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9acd39615a9a03e6f1fd6d78b816399c.jpg"
            alt="Testimonios de clientes satisfechos con licencias de software originales"
            title="Testimonios Digital License Store - Opiniones verificadas"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0078D4]/90 to-[#005a9e]/80" />
        </div>
        <div className="relative container mx-auto px-4 py-14 sm:py-20 text-center text-white w-full">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-4 sm:mb-6">
            <i className="ri-chat-quote-line text-base sm:text-lg"></i>
            <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Opiniones reales de clientes verificados</span>
          </div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">Testimonios de Nuestros Clientes</h1>
          <p className="text-sm sm:text-lg max-w-2xl mx-auto opacity-90">
            Los <strong>clientes</strong> confían en Digital License Store para sus licencias de software originales
          </p>
          <div className="flex items-center justify-center gap-3 mt-5 sm:mt-7">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <i key={star} className="ri-star-fill text-yellow-400 text-lg sm:text-2xl drop-shadow-sm" />
              ))}
            </div>
            <span className="text-2xl sm:text-3xl font-bold">{testimonialStats.averageRating}</span>
            <span className="opacity-70 text-sm">/5</span>
          </div>
          <p className="text-xs sm:text-sm opacity-70 mt-2">Basado en {testimonialStats.totalReviews} opiniones verificadas</p>
        </div>
      </section>

      <main className="flex-1">
        {/* Stats Summary */}
        <section className="py-8 sm:py-12 bg-gray-50">
          <div className="container mx-auto px-3 sm:px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
              <div className="bg-white rounded-lg p-4 sm:p-6 text-center border border-gray-200 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-[#0078D4]/10 rounded-full">
                  <i className="ri-star-fill text-[#0078D4] text-xl"></i>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900">{testimonialStats.averageRating}</div>
                <div className="text-[10px] sm:text-xs text-gray-500 mt-1">Calificación promedio</div>
              </div>
              <div className="bg-white rounded-lg p-4 sm:p-6 text-center border border-gray-200 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-green-50 rounded-full">
                  <i className="ri-chat-check-line text-green-600 text-xl"></i>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900">{testimonialStats.totalReviews}</div>
                <div className="text-[10px] sm:text-xs text-gray-500 mt-1">Opiniones totales</div>
              </div>
              <div className="bg-white rounded-lg p-4 sm:p-6 text-center border border-gray-200 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-yellow-50 rounded-full">
                  <i className="ri-thumb-up-line text-yellow-600 text-xl"></i>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900">{testimonialStats.fiveStars}%</div>
                <div className="text-[10px] sm:text-xs text-gray-500 mt-1">5 estrellas</div>
              </div>
              <div className="bg-white rounded-lg p-4 sm:p-6 text-center border border-gray-200 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-[#0078D4]/10 rounded-full">
                  <i className="ri-calendar-check-line text-[#0078D4] text-xl"></i>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900">{testimonialStats.recentReviews}</div>
                <div className="text-[10px] sm:text-xs text-gray-500 mt-1">Últimos 6 meses</div>
              </div>
            </div>
          </div>
        </section>

        {/* Rating Breakdown + Testimonials */}
        <section className="py-8 sm:py-12 bg-white">
          <div className="container mx-auto px-3 sm:px-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8">
              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200 lg:sticky lg:top-28">
                  <h2 className="font-semibold text-gray-900 mb-4 text-sm flex items-center gap-2">
                    <i className="ri-bar-chart-horizontal-line text-[#0078D4]"></i>
                    Desglose de calificaciones
                  </h2>
                  <div className="space-y-2.5 mb-5">
                    <RatingBar label="5 estrellas" percentage={testimonialStats.fiveStars} count={ratingCounts.five} />
                    <RatingBar label="4 estrellas" percentage={testimonialStats.fourStars} count={ratingCounts.four} />
                    <RatingBar label="3 estrellas" percentage={testimonialStats.threeStars} count={ratingCounts.three} />
                    <RatingBar label="2 estrellas" percentage={testimonialStats.twoStars} count={ratingCounts.two} />
                    <RatingBar label="1 estrella" percentage={testimonialStats.oneStar} count={ratingCounts.one} />
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="font-semibold text-gray-900 mb-3 text-sm flex items-center gap-2">
                      <i className="ri-filter-3-line text-[#0078D4]"></i>
                      Filtrar por
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {filters.map((f) => (
                        <button
                          key={f.key}
                          onClick={() => setFilter(f.key)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                            filter === f.key
                              ? 'bg-[#0078D4] text-white shadow-md shadow-[#0078D4]/25'
                              : 'bg-white border border-gray-300 text-gray-600 hover:border-[#0078D4] hover:text-[#0078D4]'
                          }`}
                        >
                          <i className={`${f.icon} text-[10px] ${filter === f.key ? 'text-yellow-300' : f.key === 'all' ? 'text-gray-400' : 'text-yellow-400'}`}></i>
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 mt-4 pt-4">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <i className="ri-lock-fill text-green-600"></i>
                      <span>Todas las reseñas son verificadas</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonials Grid */}
              <div className="lg:col-span-3">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-base sm:text-xl font-bold text-gray-900">
                    {filter === 'all' ? 'Todas las opiniones' : `Opiniones de ${filter} estrellas`}
                  </h2>
                  <span className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full whitespace-nowrap">{filteredTestimonials.length} resultados</span>
                </div>

                {filteredTestimonials.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                    {filteredTestimonials.map((testimonial, index) => (
                      <TestimonialCard
                        key={testimonial.id}
                        testimonial={testimonial}
                        featured={filter === 'all' && index === 0}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
                      <i className="ri-chat-off-line text-3xl text-gray-300"></i>
                    </div>
                    <p className="text-gray-500 text-sm mb-1">No hay opiniones con este filtro</p>
                    <p className="text-gray-400 text-xs">Prueba seleccionando otra categoría</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 sm:py-14 bg-gradient-to-br from-[#0078D4]/5 to-white">
          <div className="container mx-auto px-3 sm:px-4 text-center">
            <div className="max-w-2xl mx-auto">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-[#0078D4]/10 rounded-full">
                <i className="ri-heart-3-fill text-[#0078D4] text-2xl"></i>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">¿Listo para unirte a nuestros clientes satisfechos?</h2>
              <p className="text-sm text-gray-600 mb-6 max-w-lg mx-auto">
                Más de {testimonialStats.totalReviews} personas ya confían en nosotros. Obtén tus licencias originales con entrega inmediata y soporte garantizado.
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <a href="/windows" className="inline-flex items-center gap-2 bg-[#0078D4] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#005a9e] transition-colors cursor-pointer whitespace-nowrap shadow-md shadow-[#0078D4]/25">
                  <i className="ri-windows-fill"></i>
                  Ver Windows
                </a>
                <a href="/office" className="inline-flex items-center gap-2 bg-white text-[#0078D4] border border-[#0078D4]/30 px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#0078D4]/5 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-file-word-line"></i>
                  Ver Office
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Banner */}
        <section className="py-8 sm:py-12 bg-gray-50">
          <div className="container mx-auto px-3 sm:px-4">
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-4 sm:gap-8 md:gap-16">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                  <i className="ri-shield-check-fill text-green-600 text-xl"></i>
                </div>
                <div>
                  <div className="text-xs sm:text-sm font-semibold text-gray-900">Compra Segura</div>
                  <div className="text-[10px] sm:text-xs text-gray-500">Pago 100% protegido</div>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0078D4]/10 rounded-full flex-shrink-0">
                  <i className="ri-mail-send-fill text-[#0078D4] text-xl"></i>
                </div>
                <div>
                  <div className="text-xs sm:text-sm font-semibold text-gray-900">Entrega Inmediata</div>
                  <div className="text-[10px] sm:text-xs text-gray-500">Recibe por email al instante</div>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-yellow-100 rounded-full flex-shrink-0">
                  <i className="ri-award-fill text-yellow-600 text-xl"></i>
                </div>
                <div>
                  <div className="text-xs sm:text-sm font-semibold text-gray-900">Garantía de por Vida</div>
                  <div className="text-[10px] sm:text-xs text-gray-500">En licencias perpetuas</div>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                  <i className="ri-customer-service-2-fill text-green-600 text-xl"></i>
                </div>
                <div>
                  <div className="text-xs sm:text-sm font-semibold text-gray-900">Soporte 24/7</div>
                  <div className="text-[10px] sm:text-xs text-gray-500">WhatsApp, teléfono y email</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
